import React, { useEffect, useState } from "react";
import { Package, Truck, PackageCheck, Clock, RotateCcw, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatPrice } from "@/lib/currency";
import { toast } from "sonner";

const STATUS = {
  processing: { icon: Clock, cls: "text-amber-400", key: "m_track_processing" },
  shipped: { icon: Truck, cls: "text-blue-400", key: "m_track_shipped" },
  delivered: { icon: PackageCheck, cls: "text-emerald-400", key: "m_track_delivered" },
  refunded: { icon: RotateCcw, cls: "text-red-400", key: "m_track_refunded" },
};

export default function OrdersView() {
  const { t } = useI18n();
  const [orders, setOrders] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const load = () => api.get("/market/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const confirmReceipt = async (id) => {
    if (!window.confirm(t("m_confirm_receipt_q"))) return;
    setConfirming(id);
    try { await api.post(`/market/orders/${id}/confirm`); toast.success(t("m_receipt_confirmed")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("s_failed")); }
    finally { setConfirming(null); }
  };

  if (orders === null) return <p className="text-center text-sm text-[var(--rc-muted)] py-10">{t("m_loading")}</p>;
  if (orders.length === 0) return <div className="text-center py-16 text-[var(--rc-muted)]" data-testid="orders-empty"><Package size={34} className="mx-auto mb-2 text-[var(--rc-gold)]" />{t("m_no_orders")}</div>;

  return (
    <div className="space-y-2" data-testid="orders-view">
      {orders.map((o) => {
        const st = STATUS[o.track_status] || STATUS.processing;
        const Icon = st.icon;
        const steps = ["processing", "shipped", "delivered"];
        const curIdx = steps.indexOf(o.track_status);
        return (
          <div key={o.id} className="rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-4" data-testid={`order-${o.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium truncate">{(o.items || []).map((it) => `${it.qty}× ${it.title}`).join(", ")}</span>
              <span className={`flex items-center gap-1 text-xs font-semibold shrink-0 ml-2 ${st.cls}`} data-testid={`order-status-${o.id}`}><Icon size={13} /> {t(st.key)}</span>
            </div>
            <div className="text-xs text-[var(--rc-muted)] mb-3">{new Date(o.created_at).toLocaleDateString()} · {formatPrice(o.total, o.currency || "USD")}</div>
            {o.track_status !== "refunded" && (
              <div className="flex items-center gap-1">
                {steps.map((s, i) => (
                  <React.Fragment key={s}>
                    <div className={`h-2 w-2 rounded-full ${i <= curIdx ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-border)]"}`} />
                    {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < curIdx ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-border)]"}`} />}
                  </React.Fragment>
                ))}
              </div>
            )}
            {o.track_status !== "refunded" && (
              <div className="flex justify-between text-[10px] text-[var(--rc-muted)] mt-1">
                <span>{t("m_track_processing")}</span><span>{t("m_track_shipped")}</span><span>{t("m_track_delivered")}</span>
              </div>
            )}
            {o.escrow_status === "held" && !o.buyer_confirmed && o.track_status !== "refunded" && (
              <button onClick={() => confirmReceipt(o.id)} disabled={confirming === o.id} data-testid={`confirm-receipt-${o.id}`}
                className="mt-3 w-full rounded-xl bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 py-2 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
                {confirming === o.id ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} {t("m_confirm_receipt")}
              </button>
            )}
            {o.buyer_confirmed && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-400" data-testid={`order-released-${o.id}`}><ShieldCheck size={12} /> {t("m_payment_released")}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
